import React, { useState, useRef, useEffect, forwardRef } from 'react'
import PropTypes from 'prop-types'
import {
  Icon,
  iconProps,
  useUpdateRef,
  useGlobalId,
  classNames
} from '@zambezi/sdk/common'
import InputWrapper from './InputWrapper'
import { focus, componentWillReceiveProps } from './utils'

/**
 * Create a React Checkbox component.
 *
 * @param {import('./interfaces').CheckboxProps} props
 * @param {import('react').Ref} ref
 * @returns {JSX.Element}
 */
function checkbox (props, ref) {
  const {
    withLabel,
    label,
    error,
    disabled,
    className,
    onChange,
    onFocus,
    onBlur,
    checked: propsChecked,
    id: propsId,
    defaultChecked,
    name,
    iconProps
  } = props
  const [checked, setChecked] = useState(
    isUncontrolled(props) ? defaultChecked : propsChecked
  )
  const globalId = useGlobalId('zb-checkbox-')
  const [id, setId] = useState(propsId || globalId)
  const [focused, setFocused] = useState(false)
  const labelElementRef = useRef()
  const elementRef = useRef()
  const justClickedOnLabelRef = useRef(false)
  const disabledHandler = disabled ? null : true

  useUpdateRef(ref, elementRef, {
    focus: focusOptions =>
      focus(labelElementRef, elementRef, setFocused, focusOptions),
    name,
    setChecked,
    checked
  })
  useEffect(
    () =>
      componentWillReceiveProps(
        props,
        propsChecked,
        propsId,
        id,
        globalId,
        isUncontrolled,
        setChecked,
        setId
      ),
    [props, propsChecked, propsId, id, globalId]
  )

  const inputProps = {
    ...props,
    ref: elementRef,
    id,
    focused,
    checked,
    onMouseDown: disabledHandler && handleMouseDown,
    onClick: disabledHandler && handleClick,
    onMouseUp: disabledHandler && handleMouseUp,
    onBlur: disabledHandler && handleBlur,
    onFocus: disabledHandler && handleFocus,
    onChange: disabledHandler && handleChange,
    type: 'checkbox',
    rootClassName: 'zb-checkbox',
    children: (
      <span className='zb-checkbox-tick'>
        <Icon name='core-checkbox-checked' iconProps={iconProps} />
      </span>
    )
  }

  return withLabel ? (
    <div
      className={classNames(
        'zb-checkbox-labelled',
        {
          'zb-checkbox-labelled-is-focused': focused,
          'zb-checkbox-labelled-is-error': error,
          'zb-checkbox-labelled-is-disabled': disabled
        },
        className
      )}
    >
      {<InputWrapper {...inputProps} />}
      <label className={'zb-checkbox-label'} htmlFor={id} ref={labelElementRef}>
        {label}
      </label>
    </div>
  ) : (
    <InputWrapper {...inputProps} />
  )

  function handleClick (e) {
    e.preventDefault() // Prevents potential <label> wrapping this component to trigger handleChange
    handleToggle(!checked, e)
  }

  function handleMouseDown (e) {
    justClickedOnLabelRef.current = true
  }

  function handleMouseUp (e) {
    justClickedOnLabelRef.current = false
    elementRef.current.focus()
  }

  function handleChange (e) {
    handleToggle(e.target.checked, e)
  }

  function handleToggle (checked, e) {
    if (isUncontrolled(props)) {
      setChecked(checked)
    }

    onChange(e, checked)
  }

  function handleFocus (e) {
    setFocused(true)
    onFocus(e)
  }

  function handleBlur (e) {
    // Prevents blur when clicking on label
    if (justClickedOnLabelRef.current) {
      return
    }

    setFocused(false)
    onBlur(e)
  }
}

function isUncontrolled (props) {
  return props.checked === undefined
}

function noop () {}

const Checkbox = forwardRef(checkbox)

Checkbox.displayName = 'Checkbox'

Checkbox.propTypes = {
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  value: PropTypes.string,
  name: PropTypes.string,
  id: PropTypes.string,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyPress: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
  className: PropTypes.string,
  withLabel: PropTypes.bool,
  iconProps
}

Checkbox.defaultProps = {
  checked: undefined,
  disabled: false,
  defaultChecked: false,
  error: false,
  value: undefined,
  onChange: noop,
  onFocus: noop,
  onBlur: noop,
  withLabel: false
}

export default Checkbox
